// src/store/workloadStore.ts
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { useMemo } from 'react';
import { Workload, WorkloadStatus, WorkloadFilters } from '../types/workload.types';
import { WorkloadAddress, AddressStatus } from '../types/address.types';

// ============================================
// TYPES
// ============================================

interface WorkloadListState {
  workloads: Workload[];
  isLoading: boolean;
  error: string | null;
  filters: WorkloadFilters;
  sortBy: 'name' | 'createdAt' | 'updatedAt' | 'status' | 'progress';
  sortDirection: 'asc' | 'desc';
  viewMode: 'list' | 'grid' | 'map';
  selectedWorkloadIds: string[];
}

interface WorkloadDetailState {
  currentWorkload: Workload | null;
  addresses: WorkloadAddress[];
  isLoading: boolean;
  error: string | null;
  addressFilters: {
    status: AddressStatus[];
    search: string;
    tags: string[];
  };
  selectedAddressIds: string[];
  mapCenter: { lat: number; lng: number } | null;
  mapZoom: number;
}

// Address with polygon ID for multi-polygon support
interface CreationAddress {
  street: string;
  unit?: string;
  city: string;
  state: string;
  zipCode: string;
  location: { lat: number; lng: number };
  selected: boolean;
  polygonId?: string; // Which polygon this address belongs to
  streetName?: string; // Extracted street name for grouping
}

// Individual polygon territory
interface PolygonTerritory {
  id: string;
  paths: google.maps.LatLngLiteral[];
  bounds: google.maps.LatLngBoundsLiteral;
  addressCount?: number;
}

interface WorkloadCreationState {
  step: number;
  type: 'saturation' | 'csv_upload' | 'census_block' | 'draw_area' | null;
  name: string;
  projectId: string;
  surveyId: string | null;
  surveyName: string | null;
  territory: {
    type: 'polygon' | 'radius' | null;
    polygon: google.maps.LatLngLiteral[] | null;
    center: google.maps.LatLngLiteral | null;
    radius: number;
  };
  // Multi-polygon support
  polygons: PolygonTerritory[];
  addresses: CreationAddress[];
  csvFile: File | null;
  csvMapping: Record<string, string>;
  assignedTo: string[];
  assignedToNames: string[];
  assignedGroups: string[];
  listSplitting: {
    enabled: boolean;
    maxSize: number;
    autoName: boolean;
    namePrefix: string;
    splitByStreet: boolean; // Smart splitting by street
    splitByPolygon: boolean; // Each polygon becomes separate workload
  };
}

interface WorkloadStore {
  // List state
  list: WorkloadListState;

  // Detail state
  detail: WorkloadDetailState;

  // Creation state
  creation: WorkloadCreationState;

  // List actions
  setWorkloads: (workloads: Workload[]) => void;
  setListLoading: (loading: boolean) => void;
  setListError: (error: string | null) => void;
  setFilters: (filters: Partial<WorkloadFilters>) => void;
  clearFilters: () => void;
  setSortBy: (sortBy: WorkloadListState['sortBy']) => void;
  setSortDirection: (direction: 'asc' | 'desc') => void;
  setViewMode: (mode: WorkloadListState['viewMode']) => void;
  selectWorkload: (id: string) => void;
  deselectWorkload: (id: string) => void;
  selectAllWorkloads: () => void;
  clearWorkloadSelection: () => void;

  // Detail actions
  setCurrentWorkload: (workload: Workload | null) => void;
  setAddresses: (addresses: WorkloadAddress[]) => void;
  setDetailLoading: (loading: boolean) => void;
  setDetailError: (error: string | null) => void;
  setAddressFilters: (filters: Partial<WorkloadDetailState['addressFilters']>) => void;
  clearAddressFilters: () => void;
  selectAddress: (id: string) => void;
  deselectAddress: (id: string) => void;
  selectAllAddresses: () => void;
  clearAddressSelection: () => void;
  setMapCenter: (center: { lat: number; lng: number }) => void;
  setMapZoom: (zoom: number) => void;

  // Creation actions
  setCreationStep: (step: number) => void;
  setCreationType: (type: WorkloadCreationState['type']) => void;
  setCreationName: (name: string) => void;
  setCreationProject: (projectId: string) => void;
  setCreationSurvey: (surveyId: string | null, surveyName?: string | null) => void;
  setCreationTerritory: (territory: Partial<WorkloadCreationState['territory']>) => void;
  setCreationAddresses: (addresses: WorkloadCreationState['addresses']) => void;
  toggleAddressSelection: (index: number) => void;
  selectAllCreationAddresses: () => void;
  deselectAllCreationAddresses: () => void;
  setCreationCsvFile: (file: File | null) => void;
  setCreationCsvMapping: (mapping: Record<string, string>) => void;
  setCreationAssignedTo: (userIds: string[]) => void;
  setCreationAssignedToNames: (names: string[]) => void;
  setCreationAssignedGroups: (groupIds: string[]) => void;
  setListSplitting: (settings: Partial<WorkloadCreationState['listSplitting']>) => void;
  // Multi-polygon actions
  setCreationPolygons: (polygons: PolygonTerritory[]) => void;
  addCreationPolygon: (polygon: PolygonTerritory) => void;
  removeCreationPolygon: (polygonId: string) => void;
  clearCreationPolygons: () => void;
  resetCreation: () => void;

  // Global actions
  reset: () => void;
}

// Export types for external use
export type { CreationAddress, PolygonTerritory };

// ============================================
// INITIAL STATE
// ============================================

const initialListState: WorkloadListState = {
  workloads: [],
  isLoading: false,
  error: null,
  filters: {},
  sortBy: 'createdAt',
  sortDirection: 'desc',
  viewMode: 'list',
  selectedWorkloadIds: [],
};

const initialDetailState: WorkloadDetailState = {
  currentWorkload: null,
  addresses: [],
  isLoading: false,
  error: null,
  addressFilters: {
    status: [],
    search: '',
    tags: [],
  },
  selectedAddressIds: [],
  mapCenter: null,
  mapZoom: 15,
};

const initialCreationState: WorkloadCreationState = {
  step: 0,
  type: null,
  name: '',
  projectId: '',
  surveyId: null,
  surveyName: null,
  territory: {
    type: null,
    polygon: null,
    center: null,
    radius: 500, // Default 500m radius
  },
  polygons: [],
  addresses: [],
  csvFile: null,
  csvMapping: {},
  assignedTo: [],
  assignedToNames: [],
  assignedGroups: [],
  listSplitting: {
    enabled: false,
    maxSize: 100,
    autoName: true,
    namePrefix: 'List',
    splitByStreet: true,
    splitByPolygon: true,
  },
};

// ============================================
// STORE
// ============================================

export const useWorkloadStore = create<WorkloadStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        list: initialListState,
        detail: initialDetailState,
        creation: initialCreationState,

        // List actions
        setWorkloads: (workloads) =>
          set((state) => ({ list: { ...state.list, workloads } })),

        setListLoading: (isLoading) =>
          set((state) => ({ list: { ...state.list, isLoading } })),

        setListError: (error) =>
          set((state) => ({ list: { ...state.list, error } })),

        setFilters: (filters) =>
          set((state) => ({
            list: { ...state.list, filters: { ...state.list.filters, ...filters } },
          })),

        clearFilters: () =>
          set((state) => ({ list: { ...state.list, filters: {} } })),

        setSortBy: (sortBy) =>
          set((state) => ({ list: { ...state.list, sortBy } })),

        setSortDirection: (sortDirection) =>
          set((state) => ({ list: { ...state.list, sortDirection } })),

        setViewMode: (viewMode) =>
          set((state) => ({ list: { ...state.list, viewMode } })),

        selectWorkload: (id) =>
          set((state) => ({
            list: {
              ...state.list,
              selectedWorkloadIds: [...state.list.selectedWorkloadIds, id],
            },
          })),

        deselectWorkload: (id) =>
          set((state) => ({
            list: {
              ...state.list,
              selectedWorkloadIds: state.list.selectedWorkloadIds.filter((wid) => wid !== id),
            },
          })),

        selectAllWorkloads: () =>
          set((state) => ({
            list: {
              ...state.list,
              selectedWorkloadIds: (Array.isArray(state.list.workloads) ? state.list.workloads : []).map((w) => w.id),
            },
          })),

        clearWorkloadSelection: () =>
          set((state) => ({ list: { ...state.list, selectedWorkloadIds: [] } })),

        // Detail actions
        setCurrentWorkload: (currentWorkload) =>
          set((state) => ({ detail: { ...state.detail, currentWorkload } })),

        setAddresses: (addresses) =>
          set((state) => ({ detail: { ...state.detail, addresses } })),

        setDetailLoading: (isLoading) =>
          set((state) => ({ detail: { ...state.detail, isLoading } })),

        setDetailError: (error) =>
          set((state) => ({ detail: { ...state.detail, error } })),

        setAddressFilters: (filters) =>
          set((state) => ({
            detail: {
              ...state.detail,
              addressFilters: { ...state.detail.addressFilters, ...filters },
            },
          })),

        clearAddressFilters: () =>
          set((state) => ({
            detail: {
              ...state.detail,
              addressFilters: { status: [], search: '', tags: [] },
            },
          })),

        selectAddress: (id) =>
          set((state) => ({
            detail: {
              ...state.detail,
              selectedAddressIds: [...state.detail.selectedAddressIds, id],
            },
          })),

        deselectAddress: (id) =>
          set((state) => ({
            detail: {
              ...state.detail,
              selectedAddressIds: state.detail.selectedAddressIds.filter((aid) => aid !== id),
            },
          })),

        selectAllAddresses: () =>
          set((state) => ({
            detail: {
              ...state.detail,
              selectedAddressIds: (Array.isArray(state.detail.addresses) ? state.detail.addresses : []).map((a) => a.id),
            },
          })),

        clearAddressSelection: () =>
          set((state) => ({ detail: { ...state.detail, selectedAddressIds: [] } })),

        setMapCenter: (mapCenter) =>
          set((state) => ({ detail: { ...state.detail, mapCenter } })),

        setMapZoom: (mapZoom) =>
          set((state) => ({ detail: { ...state.detail, mapZoom } })),

        // Creation actions
        setCreationStep: (step) =>
          set((state) => ({ creation: { ...state.creation, step } })),

        setCreationType: (type) =>
          set((state) => ({ creation: { ...state.creation, type } })),

        setCreationName: (name) =>
          set((state) => ({ creation: { ...state.creation, name } })),

        setCreationProject: (projectId) =>
          set((state) => ({ creation: { ...state.creation, projectId } })),

        setCreationSurvey: (surveyId, surveyName) =>
          set((state) => ({ creation: { ...state.creation, surveyId, surveyName: surveyName || null } })),

        setCreationTerritory: (territory) =>
          set((state) => ({
            creation: {
              ...state.creation,
              territory: { ...state.creation.territory, ...territory },
            },
          })),

        setCreationAddresses: (addresses) =>
          set((state) => ({ creation: { ...state.creation, addresses } })),

        toggleAddressSelection: (index) =>
          set((state) => ({
            creation: {
              ...state.creation,
              addresses: state.creation.addresses.map((addr, i) =>
                i === index ? { ...addr, selected: !addr.selected } : addr
              ),
            },
          })),

        selectAllCreationAddresses: () =>
          set((state) => ({
            creation: {
              ...state.creation,
              addresses: state.creation.addresses.map((addr) => ({
                ...addr,
                selected: true,
              })),
            },
          })),

        deselectAllCreationAddresses: () =>
          set((state) => ({
            creation: {
              ...state.creation,
              addresses: state.creation.addresses.map((addr) => ({
                ...addr,
                selected: false,
              })),
            },
          })),

        setCreationCsvFile: (csvFile) =>
          set((state) => ({ creation: { ...state.creation, csvFile } })),

        setCreationCsvMapping: (csvMapping) =>
          set((state) => ({ creation: { ...state.creation, csvMapping } })),

        setCreationAssignedTo: (assignedTo) =>
          set((state) => ({ creation: { ...state.creation, assignedTo } })),

        setCreationAssignedToNames: (assignedToNames) =>
          set((state) => ({ creation: { ...state.creation, assignedToNames } })),

        setCreationAssignedGroups: (assignedGroups) =>
          set((state) => ({ creation: { ...state.creation, assignedGroups } })),

        setListSplitting: (settings) =>
          set((state) => ({
            creation: {
              ...state.creation,
              listSplitting: { ...state.creation.listSplitting, ...settings },
            },
          })),

        // Multi-polygon actions
        setCreationPolygons: (polygons) =>
          set((state) => ({ creation: { ...state.creation, polygons } })),

        addCreationPolygon: (polygon) =>
          set((state) => ({
            creation: {
              ...state.creation,
              polygons: [...state.creation.polygons, polygon],
            },
          })),

        removeCreationPolygon: (polygonId) =>
          set((state) => ({
            creation: {
              ...state.creation,
              polygons: state.creation.polygons.filter((p) => p.id !== polygonId),
              // Also remove addresses associated with this polygon
              addresses: state.creation.addresses.filter((a) => a.polygonId !== polygonId),
            },
          })),

        clearCreationPolygons: () =>
          set((state) => ({
            creation: {
              ...state.creation,
              polygons: [],
              addresses: [],
            },
          })),

        resetCreation: () =>
          set((state) => ({ creation: initialCreationState })),

        // Global actions
        reset: () =>
          set({
            list: initialListState,
            detail: initialDetailState,
            creation: initialCreationState,
          }),
      }),
      {
        name: 'workload-store',
        partialize: (state) => ({
          list: {
            filters: state.list.filters,
            sortBy: state.list.sortBy,
            sortDirection: state.list.sortDirection,
            viewMode: state.list.viewMode,
          },
        }),
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<WorkloadStore> | undefined;
          return {
            ...currentState,
            list: {
              ...currentState.list,
              ...(persisted?.list || {}),
              // Always preserve arrays from initial state
              workloads: currentState.list.workloads,
              selectedWorkloadIds: currentState.list.selectedWorkloadIds,
            },
            detail: currentState.detail,
            creation: currentState.creation,
          };
        },
      }
    ),
    { name: 'WorkloadStore' }
  )
);

// ============================================
// SELECTORS
// ============================================

export const useWorkloadList = () => useWorkloadStore((state) => state.list);
export const useWorkloadDetail = () => useWorkloadStore((state) => state.detail);
export const useWorkloadCreation = () => useWorkloadStore((state) => state.creation);

export const useFilteredWorkloads = () => {
  const { workloads, filters, sortBy, sortDirection } = useWorkloadStore(
    useShallow((state) => ({
      workloads: state.list.workloads,
      filters: state.list.filters,
      sortBy: state.list.sortBy,
      sortDirection: state.list.sortDirection,
    }))
  );

  return useMemo(() => {
    // Ensure workloads is always an array (guard against corrupted persisted state)
    const workloadArray = Array.isArray(workloads) ? workloads : [];
    let filtered = [...workloadArray];

    // Apply filters
    if (filters.status && filters.status.length > 0) {
      filtered = filtered.filter((w) => filters.status!.includes(w.status));
    }

    if (filters.assignedTo && filters.assignedTo.length > 0) {
      filtered = filtered.filter((w) =>
        w.assignedTo.some((uid) => filters.assignedTo!.includes(uid))
      );
    }

    if (filters.type && filters.type.length > 0) {
      filtered = filtered.filter((w) => filters.type!.includes(w.type));
    }

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(
        (w) =>
          w.name.toLowerCase().includes(search) ||
          w.assignedToNames?.some((name) => name.toLowerCase().includes(search))
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'createdAt':
          comparison = a.createdAt.toMillis() - b.createdAt.toMillis();
          break;
        case 'updatedAt':
          comparison = a.updatedAt.toMillis() - b.updatedAt.toMillis();
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'progress':
          comparison = a.progress.completionPercentage - b.progress.completionPercentage;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [workloads, filters, sortBy, sortDirection]);
};

export const useFilteredAddresses = () =>
  useWorkloadStore((state) => {
    // Ensure addresses is always an array (guard against corrupted persisted state)
    const addressArray = Array.isArray(state.detail.addresses) ? state.detail.addresses : [];
    let filtered = [...addressArray];
    const { addressFilters } = state.detail;

    if (addressFilters.status.length > 0) {
      filtered = filtered.filter((a) => addressFilters.status.includes(a.status));
    }

    if (addressFilters.search) {
      const search = addressFilters.search.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.fullAddress.toLowerCase().includes(search) ||
          a.street.toLowerCase().includes(search)
      );
    }

    if (addressFilters.tags.length > 0) {
      filtered = filtered.filter((a) =>
        a.tags.some((tag) => addressFilters.tags.includes(tag.name))
      );
    }

    return filtered;
  });

export default useWorkloadStore;
